import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api, { inr } from "@/api";
import { Reveal, MaskedLines, Overline } from "@/components/Motion";
import { RedDot } from "@/components/Logo";
import { usePage } from "@/settings";

export default function Workshops() {
  const [workshops, setWorkshops] = useState([]);
  const pg = usePage("workshops");

  useEffect(() => {
    api.get("/workshops").then((r) => setWorkshops(r.data)).catch(() => {});
  }, []);

  const upcoming = workshops.filter((w) => w.status === "upcoming");
  const past = workshops.filter((w) => w.status === "past");

  return (
    <main data-testid="workshops-page" className="pt-32 pb-24 px-6 md:px-12 max-w-7xl mx-auto min-h-screen">
      <Overline>Workshops & Events</Overline>
      <MaskedLines
        className="mt-6 mb-16"
        lineClassName="font-display text-5xl sm:text-6xl lg:text-8xl tracking-tight"
        lines={[pg.heading_1 || "Learn by making."]}
      />

      <Reveal>
        <h2 className="font-mono text-xs uppercase tracking-[0.25em] text-brand mb-8 flex items-center gap-3">
          <RedDot className="w-2 h-2 animate-pulse-dot" /> {pg.upcoming_label || "Upcoming"}
        </h2>
      </Reveal>
      <div className="space-y-16 mb-28">
        {upcoming.map((w) => (
          <Reveal key={w.id}>
            <Link to={`/workshops/${w.id}`} data-testid={`workshop-card-${w.id}`}
              className="group grid grid-cols-1 lg:grid-cols-12 gap-8 items-center border border-ink/15 hover:border-brand transition-colors p-6 md:p-8">
              <div className="lg:col-span-5 frame-clip spotlight aspect-[16/10]">
                <img src={w.cover} alt={w.title} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="lg:col-span-7">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink/50">{w.dates} · {w.location}</p>
                <h3 className="font-display text-3xl md:text-5xl tracking-tight mt-3 group-hover:text-brand transition-colors">{w.title}</h3>
                <p className="mt-4 text-ink/60 leading-relaxed max-w-2xl">{w.intro}</p>
                <div className="mt-6 flex flex-wrap items-center gap-6">
                  <span className="font-mono text-xs uppercase tracking-[0.2em]">{w.duration}</span>
                  <span className="font-mono text-xs uppercase tracking-[0.2em]">{inr(w.fee)}</span>
                  <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-brand">
                    Register <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </div>
            </Link>
          </Reveal>
        ))}
        {!upcoming.length && <p className="font-mono text-sm text-ink/50">New workshops are being planned — check back soon.</p>}
      </div>

      <Reveal>
        <h2 className="font-mono text-xs uppercase tracking-[0.25em] text-ink/50 mb-8">{pg.past_label || "Past workshops — archive"}</h2>
      </Reveal>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {past.map((w, i) => (
          <Reveal key={w.id} delay={i * 0.08}>
            <Link to={`/workshops/${w.id}`} data-testid={`past-workshop-${w.id}`} className="group block">
              <div className="frame-clip spotlight aspect-[16/9]">
                <img src={w.cover} alt={w.title} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="mt-4 border-t border-ink/15 pt-3 flex items-baseline justify-between gap-4">
                <h3 className="font-display text-xl md:text-2xl group-hover:text-brand transition-colors">{w.title}</h3>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/40 shrink-0">{w.dates}</span>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </main>
  );
}
